export const formatDateYMD = (d: Date) => d.toISOString().slice(0, 10);

// Traduz o código de CTA enviado pra API (ex: "LEARN_MORE") para o rótulo
// legível — usado tanto no preview do criativo quanto na revisão final,
// pra nunca mostrar o código cru pro usuário.
export const getCtaLabel = (
  cta: string | undefined,
  t: (key: string) => string,
) => {
  if (!cta) return t("Saiba mais");
  const map: Record<string, string> = {
    LEARN_MORE: t("Saiba mais"),
    WHATSAPP_MESSAGE: t("Mensagem WhatsApp"),
    SEND_MESSAGE: t("Enviar mensagem"),
    MESSAGE_PAGE: t("Enviar mensagem"),
    SIGN_UP: t("Cadastrar-se"),
    DOWNLOAD: t("Baixar"),
    SHOP_NOW: t("Comprar agora"),
    GET_OFFER: t("Obter oferta"),
    BOOK_NOW: t("Agendar agora"),
    CONTACT_US: t("Fale conosco"),
    APPLY_NOW: t("Inscrever-se"),
    SUBSCRIBE: t("Assinar"),
    CALL_NOW: t("Ligar agora"),
  };
  return map[cta] || t(cta);
};

// Extrai o ID de 11 caracteres de uma URL do YouTube em qualquer formato
// conhecido (watch?v=, youtu.be/, embed/). Retorna null se não achar um ID
// válido — usado pra decidir entre renderizar o iframe ou um fallback, em
// vez de montar um <iframe src=".../embed/undefined">. Compartilhado entre
// o preview do criativo e a etapa de revisão final, que têm cada uma seu
// próprio card de mídia.
export const getYoutubeId = (url: string): string | null => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]{11})/,
  );
  return match?.[1] || null;
};

const URL_RE = /(https?:\/\/[^\s<>()]+)/i;

export const extractFirstUrl = (text?: string | null) => {
  const s = (text || "").trim();
  if (!s) return "";
  const m = s.match(URL_RE);
  if (!m) return "";
  return (m[1] || "").replace(/[)\].,;!?}]+$/g, "");
};

export const isWhatsappUrl = (url: string) => {
  const u = (url || "").toLowerCase();
  return (
    u.includes("wa.me/") ||
    u.includes("api.whatsapp.com/") ||
    u.includes("whatsapp.com/")
  );
};

export const extractWhatsappPhoneFromUrl = (url: string) => {
  try {
    const m = url.match(/wa\.me\/(\d+)/i);
    if (m?.[1]) return m[1];
    const u = new URL(url);
    const phone = u.searchParams.get("phone");
    if (phone) return phone.replace(/\D/g, "");
  } catch {
    /* ignore */
  }
  return "";
};

export const extractWhatsappTextFromUrl = (url: string) => {
  try {
    const u = new URL(url);
    const text = u.searchParams.get("text");
    return text ? decodeURIComponent(text) : "";
  } catch {
    return "";
  }
};

export const resolveThumbnail = (
  url: string,
  thumbnail: string,
  isVideo: boolean,
) => {
  if (
    isVideo &&
    (!thumbnail || thumbnail === url) &&
    url.includes("/api/videos/")
  ) {
    // Aponta para o endpoint do backend que usa OpenCV para extrair o frame do vídeo
    return `${url}/thumbnail`;
  }
  return thumbnail || url;
};

export const extractPostMediaUrl = (
  post?: any,
): { url: string; type: "image" | "video"; thumbnail: string } => {
  if (!post) return { url: "", type: "image", thumbnail: "" };

  // 1. Direct fields (Common in legacy/simple objects)
  const directUrl =
    post.media_url || post.image_url || post.thumbnail_url || post.thumbnail;
  const directThumb =
    post.thumbnail_url || post.thumbnail || post.image_url || post.media_url;

  if (typeof directUrl === "string" && directUrl.trim()) {
    const isVideo = !!directUrl.match(/\.(mp4|webm|mov|ogg)$/i);
    return {
      url: directUrl.trim(),
      type: isVideo ? "video" : "image",
      thumbnail: resolveThumbnail(
        directUrl.trim(),
        (directThumb || directUrl).trim(),
        isVideo,
      ),
    };
  }

  // 2. Video Info Support
  const videoInfo: any = post?.video_info;
  if (videoInfo && typeof videoInfo === "object") {
    const vCandidates = [
      videoInfo.url,
      videoInfo.video_url,
      videoInfo.public_url,
      videoInfo.secure_url,
      videoInfo.cdn_url,
    ];
    const vThumb =
      videoInfo.thumbnail_url ||
      videoInfo.thumbnail ||
      post.thumbnail_url ||
      post.thumbnail;
    for (const c of vCandidates) {
      const s = String(c || "").trim();
      if (s)
        return {
          url: s,
          type: "video",
          thumbnail: resolveThumbnail(s, String(vThumb || "").trim(), true),
        };
    }
  }

  // 3. Image Info Support
  const imageInfo: any = post?.image_info;
  if (imageInfo && typeof imageInfo === "object") {
    const iCandidates = [
      imageInfo.url,
      imageInfo.image_url,
      imageInfo.public_url,
      imageInfo.secure_url,
      imageInfo.cdn_url,
    ];
    for (const c of iCandidates) {
      const s = String(c || "").trim();
      if (s) return { url: s, type: "image", thumbnail: s };
    }
  }

  // 4. Array Support (post_media or media_gallery list)
  const mediaList = post.post_media || post.media_gallery;
  if (Array.isArray(mediaList) && mediaList.length > 0) {
    const firstMedia = mediaList[0];
    const mUrl =
      firstMedia.url || firstMedia.media_url || firstMedia.public_url;
    const mThumb = firstMedia.thumbnail_url || firstMedia.thumbnail || mUrl;
    if (mUrl) {
      const isVideo =
        firstMedia.type === "video" ||
        firstMedia.media_type === "video" ||
        !!String(mUrl).match(/\.(mp4|webm|mov|ogg)$/i);
      return {
        url: String(mUrl).trim(),
        type: isVideo ? "video" : "image",
        thumbnail: resolveThumbnail(
          String(mUrl).trim(),
          String(mThumb || "").trim(),
          isVideo,
        ),
      };
    }
  }
  return { url: "", type: "image", thumbnail: "" };
};

export const extractPostMediaUrls = (post?: any) => {
  if (!post) return [];

  const results: { url: string; type: "image" | "video"; thumbnail: string }[] =
    [];
  console.log(
    "🔍 [ADS UTILS] Extracting media from post:",
    post?.id,
    "Media gallery:",
    post?.media_gallery,
  );

  // 1. Array Support (post_media or media_gallery list) - Most common for multi-image
  const mediaList = post.post_media || post.media_gallery;
  if (Array.isArray(mediaList) && mediaList.length > 0) {
    mediaList.forEach((m: any) => {
      const mUrl = m.url || m.media_url || m.public_url;
      const mThumb = m.thumbnail_url || m.thumbnail || mUrl;
      if (mUrl) {
        const isVideo =
          m.type === "video" ||
          m.media_type === "video" ||
          !!String(mUrl).match(/\.(mp4|webm|mov|ogg)$/i);
        results.push({
          url: String(mUrl).trim(),
          type: isVideo ? "video" : "image",
          thumbnail: resolveThumbnail(
            String(mUrl).trim(),
            String(mThumb || "").trim(),
            isVideo,
          ),
        });
      }
    });
  }

  console.log("🔍 [ADS UTILS] Extracted results:", results);

  // 2. Fallback to single media if results still empty
  if (results.length === 0) {
    const single = extractPostMediaUrl(post);
    if (single.url) {
      results.push(single);
    }
  }

  return results;
};

/**
 * Sugere conteúdos de anúncio inteligentes baseados no post.
 */
export const suggestAdContentFromPost = (post: any, platform: string) => {
  const content = (post.content || "").trim();
  const theme = (post.theme || "").trim();
  const cta = (post.call_to_action || "").trim();
  const hashtags = (post.hashtags || "").trim();

  // Limpeza de texto: remove hashtags e URLs, mas preserva formatação de quebra de linha
  const cleanContent = content
    .replace(/#\w+/g, "")
    .replace(/(https?:\/\/[^\s]+)/g, "")
    .replace(/[ \t]+/g, " ") // Apenas espaços na mesma linha
    .replace(/\n{3,}/g, "\n\n") // Limita múltiplas quebras de linha
    .trim();

  const lines = cleanContent
    .split(/[.\n!]/)
    .filter((l: string) => l.trim().length > 10);
  const firstSentence = lines[0]?.trim() || cleanContent.slice(0, 40);
  const question = lines.find((l: string) => l.includes("?"))?.trim() || "";

  // 1. Headline
  let suggestedHeadline = theme || firstSentence;
  if (platform === "google_ads") {
    suggestedHeadline = suggestedHeadline.slice(0, 30);
  } else {
    suggestedHeadline = suggestedHeadline.slice(0, 255);
  }

  // 2. Description/Subtítulo
  let suggestedSub =
    question || (lines[1] ? lines[1].trim() : "Confira nossa solução.");
  if (platform === "google_ads") {
    suggestedSub = suggestedSub.slice(0, 90);
  }

  // 3. Message/Legenda/Snippet
  let suggestedMessage = cleanContent;
  if (platform === "google_ads") {
    // Para o campo "Snippet Google" (que no editor usa o state message), queremos benefícios curtos
    const words = cleanContent.split(/\s+/).filter((w: string) => w.length > 5);
    suggestedMessage = words.slice(0, 10).join(", ") + ". Otimize resultados.";
  } else {
    // Para Meta, adicionamos quebras de linha, o CTA e as hashtags se houverem
    if (cta) suggestedMessage += "\n\n" + cta;
    if (hashtags) suggestedMessage += "\n\n" + hashtags;
  }

  return {
    headline: suggestedHeadline,
    description: suggestedSub,
    message: suggestedMessage,
    cleanContent,
  };
};
